import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import _spliceInstanceProperty from "@babel/runtime-corejs3/core-js/instance/splice";
import { computed, defineComponent, onMounted, ref, watch, watchEffect } from "@vue/composition-api";
import RenewalTrackingContainerLoader from "./renewal-tracking-container-loader.vue";
import MaterialInput from "@Shared/components/inputs/material-input.vue";
import AgentsSelect from "@Broker/@Modules/agents/components/agents-select.vue";
import SelectLevel from "../select-level.vue";
import SelectFormat from "../select-format.vue";
import SelectMortgageType from "../select-mortgage-type.vue";
import SelectRequestType from "../select-request-type.vue";
import SelectLender from "../select-lender.vue";
import DateSection from "@Shared/components/sections/date-section.vue";
import Field from "@Shared/components/sections/field.vue";
import ReportToolbar from "../report-toolbar.vue";
import ReportTabLayout from "../report-tab-layout.vue";
import { useI18n } from "vue-i18n-composable";
import { useMultipleSelect } from "@Shared/@Composables/generic/useMultipleSelect";
import { REPORT_GROUP_BY_E, REPORT_LEVEL_E, REPORT_SORT_BY_E } from "@Broker/@Types/reports/types";
import { useReportsStoreGetters } from "@Broker/@Modules/clients/reports/store/reports-helpers";
import { useAccountStoreGetters } from "@Broker/@Modules/main/store/account/account-helpers";
import * as ReportsGetters from "@Broker/@Modules/clients/reports/store/reports-getters";
import * as AccountGetters from "@Broker/@Modules/main/store/account/account-getters";
import { capitalize, cloneDeep } from "lodash";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { useVuelidate } from "@Shared/@Composables/validations/useVuelidate";
import { useReportDefaultSettings } from "@Broker/@Composables/reports/useReportDefaultSettings";
import vSelectField from "@Shared/components/form-elements/fields/v-select-field.vue";
export default defineComponent({
  name: "RenewalTrackingContainer",
  components: {
    ReportTabLayout,
    ReportToolbar,
    AgentsSelect,
    SelectLevel,
    SelectFormat,
    SelectLender,
    SelectMortgageType,
    SelectRequestType,
    DateSection,
    Field,
    MaterialInput,
    RenewalTrackingContainerLoader,
    vSelectField
  },
  mixins: [validationMixin],
  validations() {
    return {
      localRenewalTrackingData: {
        agent: {
          required: requiredIf(() => this.showAgentsSelect)
        },
        format: {
          required
        },
        level: {
          required
        },
        lender: {
          required
        },
        mortgageType: {
          required
        },
        requestType: {
          required
        },
        groupBy: {
          required
        },
        sortBy: {
          required
        },
        dateFrom: {
          required
        },
        dateTo: {
          required
        }
      }
    };
  },
  props: {
    runningReport: {
      type: Object,
      default: () => ({
        loading: false,
        loadingSucceeded: true
      })
    }
  },
  setup(props, {
    emit
  }) {
    const {
      t,
      tc
    } = useI18n();
    const {
      handleVSelectMultipleChange
    } = useMultipleSelect();
    const {
      v$
    } = useVuelidate();
    const loading = ref(false);
    const settingDefault = ref({
      loading: false,
      loadingSucceeded: true
    });
    const unsyncedAppsModal = ref();
    const action = ref();
    const {
      CURRENT_ACCOUNT_IS_BROKER,
      CURRENT_USER_USERNAME
    } = useAccountStoreGetters([AccountGetters.CURRENT_ACCOUNT_IS_BROKER, AccountGetters.CURRENT_USER_USERNAME]);
    const {
      GET_RENEWAL_TRACKING_STRUCTURE
    } = useReportsStoreGetters([ReportsGetters.GET_RENEWAL_TRACKING_STRUCTURE]);
    const renewalTrackingStructure = cloneDeep(GET_RENEWAL_TRACKING_STRUCTURE.value);
    const localRenewalTrackingData = ref(renewalTrackingStructure);
    const agentsOptions = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const handleSelectAgentChange = (value, type) => localRenewalTrackingData.value.agent = handleVSelectMultipleChange(value, type, localRenewalTrackingData.value.agent, true);
    const handleChangeDates = dates => {
      const {
        end,
        start
      } = dates;
      localRenewalTrackingData.value.dateFrom = start;
      localRenewalTrackingData.value.dateTo = end;
    };
    const isValid = computed(() => {
      var _v$$value;
      return !((_v$$value = v$.value) != null && _v$$value.$invalid);
    });
    const sortBySelectOptions = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const groupBySelectOptions = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const groupByOptions = computed(() => [{
      id: REPORT_GROUP_BY_E.MORTGAGE_TYPE,
      text: t("application-reports.renewal-tracking.sortByOptions.mortgageType")
    }, {
      id: REPORT_GROUP_BY_E.LENDER,
      text: t("application-reports.renewal-tracking.sortByOptions.lender")
    }, {
      id: REPORT_GROUP_BY_E.AGENT,
      text: t("application-reports.renewal-tracking.sortByOptions.agent")
    }]);
    const sortByOptions = computed(() => [{
      id: REPORT_SORT_BY_E.APPLICATION_ID,
      text: t("application-reports.renewal-tracking.sortByOptions.applicationId")
    }, {
      id: REPORT_SORT_BY_E.AGENT,
      text: t("application-reports.renewal-tracking.sortByOptions.agent")
    }, {
      id: REPORT_SORT_BY_E.MORTGAGE_TYPE,
      text: t("application-reports.renewal-tracking.sortByOptions.mortgageType")
    }, {
      id: REPORT_SORT_BY_E.LENDER,
      text: t("application-reports.renewal-tracking.sortByOptions.lender")
    }, {
      id: REPORT_SORT_BY_E.MATURITY_DATE,
      text: t("application-reports.renewal-tracking.sortByOptions.maturityDate")
    }, {
      id: REPORT_SORT_BY_E.DEAL_STATUS,
      text: t("application-reports.renewal-tracking.sortByOptions.dealStatus")
    }]);
    const showAgentsSelect = computed(() => {
      return CURRENT_ACCOUNT_IS_BROKER.value && localRenewalTrackingData.value.level === REPORT_LEVEL_E.AGENT;
    });
    const allAgentsOption = {
      id: "all",
      username: "all",
      givenName: capitalize(tc("global.words.all", 1)),
      familyName: ""
    };
    onMounted(() => {
      setDefaultAgentIfNeeded();
      action.value = "";
    });
    const setDefaultAgentIfNeeded = () => {
      if (!CURRENT_ACCOUNT_IS_BROKER.value) localRenewalTrackingData.value.agent = [CURRENT_USER_USERNAME.value];
    };
    const primaryOptions = computed(() => {
      var _context;
      return _filterInstanceProperty(_context = sortByOptions.value).call(_context, val => val.id !== localRenewalTrackingData.value.sortBy[1]);
    });
    const secondaryOptions = computed(() => {
      var _context2;
      return _filterInstanceProperty(_context2 = sortByOptions.value).call(_context2, val => val.id !== localRenewalTrackingData.value.sortBy[0]);
    });
    const handleChangeSortBy = ({
      id
    }, sortByIndex) => {
      var _context3;
      _spliceInstanceProperty(_context3 = localRenewalTrackingData.value.sortBy).call(_context3, sortByIndex, 1, id);
    };
    const handleChangeGroupBy = ({
      id
    }) => {
      localRenewalTrackingData.value.groupBy = id;
    };
    const handleClearSelection = () => {
      localRenewalTrackingData.value = cloneDeep(renewalTrackingStructure);
      setDefaultAgentIfNeeded();
      action.value = "clear";
    };
    const {
      loadReportDefaultValues,
      updateReportDefaults
    } = useReportDefaultSettings({
      reportType: "renewalTracking",
      reportDataRef: localRenewalTrackingData
    });
    const handleResetToDefault = () => {
      loadReportDefaultValues();
      setDefaultAgentIfNeeded();
      action.value = "reset";
    };
    const handleSetDefault = async () => {
      settingDefault.value.loading = true;
      const {
        level,
        agent: agents,
        lender,
        mortgageType,
        requestType,
        dateFrom: maturityDateFrom,
        dateTo: maturityDateTo,
        groupBy,
        sortBy: [sortByPrimary, sortBySecondary],
        format
      } = localRenewalTrackingData.value;
      await updateReportDefaults({
        agents,
        level,
        maturityDateFrom,
        maturityDateTo,
        lender,
        mortgageType,
        requestType,
        groupBy,
        sortByPrimary,
        sortBySecondary,
        format
      });
      settingDefault.value.loading = false;
    };
    const handleRunReport = async () => {
      emit("run-report", {
        reportType: "renewal-tracking",
        data: localRenewalTrackingData.value
      });
    };
    watch(() => localRenewalTrackingData.value.level, (selectedLevel, oldSelectedLevel) => {
      if (selectedLevel === oldSelectedLevel) return;
      if (selectedLevel === REPORT_LEVEL_E.BROKERAGE) {
        localRenewalTrackingData.value.agent = "all";
      }
    });
    watchEffect(() => {
      if (!localRenewalTrackingData.value.groupBy) {
        localRenewalTrackingData.value.groupBy = REPORT_GROUP_BY_E.AGENT;
      }
      if (!localRenewalTrackingData.value.sortBy[0]) {
        localRenewalTrackingData.value.sortBy[0] = REPORT_SORT_BY_E.LENDER;
      }
      if (!localRenewalTrackingData.value.sortBy[1]) {
        localRenewalTrackingData.value.sortBy[1] = REPORT_SORT_BY_E.APPLICATION_ID;
      }
    });
    return {
      loading,
      localRenewalTrackingData,
      sortByOptions,
      sortBySelectOptions,
      groupBySelectOptions,
      groupByOptions,
      allAgentsOption,
      handleChangeDates,
      handleChangeSortBy,
      handleChangeGroupBy,
      handleSelectAgentChange,
      handleClearSelection,
      handleResetToDefault,
      handleSetDefault,
      handleRunReport,
      setDefaultAgentIfNeeded,
      agentsOptions,
      showAgentsSelect,
      settingDefault,
      isValid,
      v$,
      unsyncedAppsModal,
      action,
      primaryOptions,
      secondaryOptions
    };
  }
});